// ─────────────────────────────────────────────────────────────────────────────
// Ludo — shared WebSocket protocol. Single source of truth for both the server
// and the client. Board geometry lives in board.ts.
// ─────────────────────────────────────────────────────────────────────────────

import type { Color } from './board.js';

export const ROOM_CODE_LENGTH = 6;
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 4;

/** Turn-timer options the host can pick in the lobby (seconds). */
export const TURN_TIME_OPTIONS = [15, 30, 45] as const;
export const DEFAULT_TURN_TIME_SECONDS = 30;

export type PlayerId = string;

export type Phase = 'lobby' | 'playing' | 'gameover';

/** Bot skill levels. Easy = random, Medium = human-like, Hard = optimal. */
export type BotDifficulty = 'easy' | 'medium' | 'hard';

export const BOT_DIFFICULTIES: BotDifficulty[] = ['easy', 'medium', 'hard'];

export interface PlayerPublic {
  id: PlayerId;
  name: string;
  color: Color;
  ready: boolean;
  connected: boolean;
  /** The four token distances: 0 = yard, 1..51 = track, 52..56 = home lane,
   *  57 = finished in the centre. */
  tokens: number[];
  /** 1st, 2nd, ... — set only when the player finishes; null otherwise. */
  rank: number | null;
  /** True for server-driven bots (no socket). */
  isBot: boolean;
  /** Skill level for bots; null for humans. */
  botDifficulty: BotDifficulty | null;
}

export interface ChatEntry {
  id: string;
  authorId: PlayerId | null; // null for system messages
  authorName: string;
  text: string;
  kind: 0 | 1;
}

/** Last completed roll, used by the client to animate dice + moves. */
export interface RollEvent {
  color: Color;
  value: number;
  /** Captured player id, if this roll sent someone back to the yard. */
  capturedId: PlayerId | null;
  forfeited: boolean;
}

export interface RoomSnapshot {
  roomCode: string;
  hostId: PlayerId;
  /** Turn-timer duration in milliseconds (host-configurable). */
  turnTimeoutMs: number;
  /** Epoch ms when the current turn's timer expires; null when no turn timer is active. */
  turnDeadlineAt: number | null;
  phase: Phase;
  players: PlayerPublic[];
  /** Whose turn it is to roll. */
  currentColor: Color | null;
  /** The active roll value, 0 while waiting for a throw. */
  dice: number;
  /** True once the current player has thrown and must pick a token. */
  waitingForMove: boolean;
  /** Rolled with no legal moves — the turn is ending, no re-rolls allowed. */
  passPending: boolean;
  /** Set while the current player enjoys an extra turn (last roll was a 6). */
  extraTurn: boolean;
  /** Channel for the last roll's outcome; cleared before the next turn. */
  lastRoll: RollEvent | null;
  /** Last captured victim, kept until the next roll (drives the capture cue). */
  lastCapture: { victimId: PlayerId; color: Color } | null;
  winnerId: PlayerId | null;
  chat: ChatEntry[];
}

/** Everything a single player is allowed to know on top of the snapshot. */
export interface PrivateState {
  youCanRoll: boolean;
  /** Token indices the current player may move right now. */
  movableTokens: number[];
}

// ── Client → Server ──────────────────────────────────────────────────────────

export type ClientMessage =
  | { type: 'CREATE_ROOM'; playerName: string }
  | { type: 'JOIN_ROOM'; roomCode: string; playerName: string }
  | { type: 'RECONNECT'; roomCode: string; playerId: PlayerId; identity: string }
  | { type: 'SET_READY'; ready: boolean }
  | { type: 'START_GAME' }
  | { type: 'SET_TIME_LIMIT'; seconds: number }
  | { type: 'ROLL_DICE' }
  | { type: 'MOVE_TOKEN'; tokenIndex: number }
  | { type: 'SEND_CHAT'; text: string }
  | { type: 'LEAVE_ROOM' }
  | { type: 'ADD_BOT'; difficulty: BotDifficulty }
  | { type: 'REMOVE_BOT'; playerId: PlayerId }
  | { type: 'SET_BOT_DIFFICULTY'; playerId: PlayerId; difficulty: BotDifficulty };

// ── Server → Client ──────────────────────────────────────────────────────────

export type ServerMessage =
  | { type: 'ERROR'; code: string; message: string }
  | { type: 'ROOM_CREATED'; roomCode: string; playerId: PlayerId; identity: string }
  | { type: 'ROOM_JOINED'; roomCode: string; playerId: PlayerId; identity: string }
  | { type: 'RECONNECTED'; playerId: PlayerId }
  | { type: 'ROOM_SNAPSHOT'; snapshot: RoomSnapshot }
  | { type: 'PRIVATE_STATE'; state: PrivateState }
  | { type: 'GAME_STARTED'; snapshot: RoomSnapshot; state: PrivateState }
  | { type: 'GAME_ENDED'; snapshot: RoomSnapshot }
  | { type: 'CHAT'; entry: ChatEntry }
  | { type: 'PLAYER_JOINED'; player: PlayerPublic }
  | { type: 'PLAYER_LEFT'; playerId: PlayerId };

// ── Helpers ──────────────────────────────────────────────────────────────────

export function generateRoomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no confusing 0/O/1/I
  let out = '';
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

export function isClientMessage(raw: unknown): raw is ClientMessage {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    typeof (raw as { type?: unknown }).type === 'string'
  );
}